/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { t } from 'i18next';
import { Button, Card, Col, Divider, Form, Input, InputNumber, Row, Select, Typography } from 'antd';
import { DeleteIcon } from '../../Icons/DeleteIcon';
import { PlusIcon } from '../../Icons/PlusIcon';

// Misc imports
import { NLP_OPENAI_CHAT_MESSAGES_ROLES, NLP_OPENAI_MODEL_CATEGORIES, NLP_OPENAI_TRAINING_TYPES } from '../../../constants/main';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../../constants/regex';

// Constant declarations
const { Text } = Typography;
const { Option } = Select;

const OpenAiProviderForm = () => {
  return (
    <Fragment>
      <Divider orientation="left" className="mb-8">
        <Text type="secondary" className="fs-base">{t('Provider Settings')}</Text>
      </Divider>

      <Form.Item
        label={t('Model Category')}
        name="modelCategory"
        required
        rules={[{
          required: true
        }]}
      >
        <Select
          placeholder={t('Choose a model category')}
          getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
        >
          {Object.values(NLP_OPENAI_MODEL_CATEGORIES).map(category => (
            <Option key={category.key} value={category.key}>
              {category.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={t('Model')}
        name="model"
        required
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input placeholder={t('Enter the model')} />
      </Form.Item>

      <Form.Item
        label={t('Api Key')}
        name="apiKey"
        required
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Input.TextArea
          autoSize={{ minRows: 4, maxRows: 4 }}
          rows={4}
          placeholder={t('Enter the api key')}
          allowClear
        />
      </Form.Item>
    </Fragment>
  );
};

const OpenAiProviderConfigurationForm = ({ form }) => {
  const [isCustomPrompt, setIsCustomPrompt] = useState(false);
  return (
    <Fragment>
      <Form.Item
        label={t('Training type')}
        name="trainingType"
        required
        rules={[{
          required: true,
          type: 'string',
          whitespace: true
        }]}
      >
        <Select
          placeholder={t('Enter the type')}
          onChange={(e) => e === NLP_OPENAI_TRAINING_TYPES.CUSTOM_PROMPT.key || e === NLP_OPENAI_TRAINING_TYPES.ONE_SHOT.key ? setIsCustomPrompt(true) : setIsCustomPrompt(false)}
          getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
        >
          {
            Object.values(NLP_OPENAI_TRAINING_TYPES).map(type => (
              <Option key={type.key} value={type.key}>
                {type.label}
              </Option>
            ))
          }
        </Select>
      </Form.Item>

      {
        form.getFieldValue('trainingType') === NLP_OPENAI_TRAINING_TYPES.FEW_SHOT.key && (
          <Form.Item
            label={t('Examples')}
            name="numberOfExamples"
            required
            rules={[{
              required: true,
              type: 'number',
              whitespace: true
            }]}
          >
            <InputNumber min={1} step={1} />
          </Form.Item>
        )
      }

      {
        form.getFieldValue('modelCategory') === NLP_OPENAI_MODEL_CATEGORIES.CHAT.key && (
          <Fragment>
            <Form.Item
              label={t('System Message')}
              name={['prompt', 'systemMessage']}
              rules={[{
                required: false,
                type: 'string',
                whitespace: true
              }]}
            >
              <Input.TextArea
                autoSize={{ minRows: 4 }}
                rows={4}
                placeholder={t('Enter your system message')}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label={t('Messages')}
            >
              <Form.List
                name={['prompt', 'messages']}
                rules={[{
                  required: false,
                  type: 'array'
                }]}
              >
                {(messageFields, { add: addMessage, remove: removeMessage }) => (
                  <Fragment>
                    {messageFields.map(({ key: messageKey, name: messageName, ...restMessageField }) => (
                      <Card
                        size="small"
                        key={messageKey}
                        className="bg-layout-gray shadow-none position-relative mb-4"
                      >
                        <Row>
                          <Col span={24}>
                            <Form.Item
                              label={t('Role')}
                              name={[messageName, 'role']}
                              className="mb-1"
                              rules={[{
                                required: false,
                                type: 'string',
                                whitespace: true
                              }]}
                            >
                              <Select
                                placeholder={t('Select a role')}
                              >
                                {Object.values(NLP_OPENAI_CHAT_MESSAGES_ROLES).map(role => (
                                  <Option key={role.key} value={role.key}>
                                    {role.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>

                          <Col span={24}>
                            <Form.Item
                              label={t('Message')}
                              name={[messageName, 'content']}
                              className="mb-1"
                              rules={[{
                                required: false,
                                type: 'string',
                                whitespace: true
                              }]}
                            >
                              <Input.TextArea
                                autoSize={{ minRows: 3, maxRows: 7 }}
                                placeholder={t('Enter your message')}
                                allowClear
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Button
                          className="float-right bordered border-radius-base mt-2"
                          type="text"
                          onClick={() => removeMessage(messageName)}
                          icon={<DeleteIcon />}
                        />
                      </Card>
                    ))}

                    <Button type="dashed" onClick={() => addMessage()}>
                      <span className="mr-2">
                        <PlusIcon />
                      </span>
                      <span>
                        {t('Add Message')}
                      </span>
                    </Button>
                  </Fragment>
                )}
              </Form.List>
            </Form.Item>
          </Fragment>
        )
      }

      <Form.Item
        label={t('Stop')}
        name="stop"
        rules={[
          {
            validator: (_, value) => {
              if (value?.length > 4) {
                return Promise.reject(new Error(t('Put up to 4 values only.')));
              }
              return Promise.resolve();
            }
          }
        ]}
      >
        <Select
          mode="tags"
          placeholder={t('Stop phrases')}
          allowClear
          onInputKeyDown={(e) => {
            if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
              e.preventDefault();
              return false;
            }
          }}
        />
      </Form.Item>

      <Form.Item
        label={t('Temperature')}
        name="temperature"
      >
        <InputNumber
          min={0}
          max={1}
          step="0.01"
        />
      </Form.Item>

      <Form.Item
        label={t('Max Tokens')}
        name="maxTokens"
      >
        <InputNumber min={1} />
      </Form.Item>

      <Form.Item
        label={t('Log Probs')}
        name="logprobs"
      >
        <InputNumber
          min={0}
          max={5}
        />
      </Form.Item>

      {
        isCustomPrompt ? (
          <Form.Item
            label={t('Edit Prompt')}
            name={['prompt', 'customPromptAddition']}
          >
            <Input.TextArea
              autoSize={{ minRows: 4, maxRows: 4 }}
              rows={4}
              placeholder={t('Editable prompt')}
              allowClear
            />
          </Form.Item>
        ) : null
      }
    </Fragment>
  );
};

OpenAiProviderConfigurationForm.propTypes = {
  form: PropTypes.object
};

export { OpenAiProviderForm, OpenAiProviderConfigurationForm };
